import type { DentalServiceCatalog } from '../../types';

interface ServiceCardProps {
  service: DentalServiceCatalog;
  onEdit: (service: DentalServiceCatalog) => void;
  onDelete: (service: DentalServiceCatalog) => void;
  canEdit: boolean;
}

// Category badges with icons
const categoryInfo: Record<string, { label: string; color: string; icon: string }> = {
  diagnostic: { label: 'Greining', color: 'bg-blue-100 text-blue-800', icon: '🔍' },
  preventive: { label: 'Forvarnarmeðferð', color: 'bg-green-100 text-green-800', icon: '🛡️' },
  restorative: { label: 'Viðgerð', color: 'bg-amber-100 text-amber-800', icon: '🔧' },
  endodontics: { label: 'Rótarmeðferð', color: 'bg-purple-100 text-purple-800', icon: '🦷' },
  periodontics: { label: 'Tannholdslækning', color: 'bg-pink-100 text-pink-800', icon: '💉' },
  oral_surgery: { label: 'Munnholsaðgerð', color: 'bg-red-100 text-red-800', icon: '⚕️' },
};

export function ServiceCard({ service, onEdit, onDelete, canEdit }: ServiceCardProps) {
  const category = service.category && categoryInfo[service.category] ? categoryInfo[service.category] : {
    label: service.category || 'Annað',
    color: 'bg-gray-100 text-gray-800',
    icon: '📋'
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} mín`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} klst`;
    return `${hours} klst ${mins} mín`;
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('is-IS', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price) + ' kr';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-mono text-gray-500 bg-gray-50 px-2 py-1 rounded">
              {service.cdt_code}
            </span>
            <span className={`text-xs px-2 py-1 rounded-full ${category.color}`}>
              {category.icon} {category.label}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {service.name_is || service.name}
          </h3>
          {service.description && (
            <p className="text-sm text-gray-500 italic">
              {service.description}
            </p>
          )}
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(service)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              title="Breyta"
            >
              Breyta
            </button>
            <button
              onClick={() => onDelete(service)}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
              title="Eyða"
            >
              Eyða
            </button>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Tími:</span>
          <span className="ml-2 font-medium text-gray-900">
            {formatDuration(service.default_duration_minutes)}
          </span>
        </div>
        <div>
          <span className="text-gray-500">Verð:</span>
          <span className="ml-2 font-medium text-gray-900">
            {formatPrice(service.base_price || 0)}
          </span>
        </div>
      </div>

      {/* Description */}
      {service.description && (
        <p className="mt-4 text-sm text-gray-600 line-clamp-2">
          {service.description}
        </p>
      )}
    </div>
  );
}
